// eslint-disable-next-line no-restricted-imports
import {reportError} from '@github-ui/failbot'
import {sendEvent} from '@github-ui/hydro-analytics'
import {useAnalytics} from '@github-ui/use-analytics'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {AlertIcon} from '@primer/octicons-react'
/* eslint-disable-next-line primer-react/no-deprecated-flash -- Existing Flash usage should be migrated to Banner. */
import {Dialog, Flash, FormControl, Spinner, Textarea} from '@primer/react'
import {type RefObject, useCallback, useState} from 'react'

import {LABELS} from './constants/labels'
import styles from './CopilotGenerateDescriptionDialog.module.css'

export type CopilotGenerateDescriptionDialogProps = {
  isOpen: boolean
  onClose: () => void
  onGenerate: (title: string, description: string) => void
  repoOwner: string
  repoName: string
  title: string
  body: string
  returnFocusRef: RefObject<HTMLButtonElement | null>
}

export function CopilotGenerateDescriptionDialog({
  isOpen,
  onClose,
  onGenerate,
  repoOwner,
  repoName,
  title,
  body,
  returnFocusRef,
}: CopilotGenerateDescriptionDialogProps) {
  const [copilotGenerateContext, setCopilotGenerateContext] = useState('')
  const [isGeneratingDescription, setIsGeneratingDescription] = useState(false)
  const [generationError, setGenerationError] = useState<string | null>(null)
  const {sendAnalyticsEvent} = useAnalytics()

  // Validation: at least one of title, body, or additional context must have content
  const hasValidInput = !!(title?.trim() || body?.trim() || copilotGenerateContext?.trim())

  const handleClose = useCallback(() => {
    setCopilotGenerateContext('')
    setGenerationError(null)
    onClose()
  }, [onClose])

  const handleGenerateDescription = useCallback(async () => {
    sendAnalyticsEvent('analytics.click', 'ISSUE_CREATE_GENERATE_DESCRIPTION_WITH_COPILOT', {
      repoNWO: `${repoOwner}/${repoName}`,
      hasTitle: !!title,
      hasExistingDescription: !!body,
      hasAdditionalContext: !!copilotGenerateContext,
    })

    setIsGeneratingDescription(true)
    setGenerationError(null)

    try {
      const response = await verifiedFetchJSON(`/${repoOwner}/${repoName}/issues/new/generate_copilot_description`, {
        method: 'POST',
        body: {
          title,
          body,
          context: copilotGenerateContext,
        },
      })

      if (!response.ok) {
        setGenerationError(LABELS.copilotGenerateError)
        sendGenerateDescriptionEvent('response_not_ok')
        return
      }

      const responseData = await response.json()
      const generatedTitle = responseData.title
      const generatedDescription = responseData.description

      if (
        !generatedTitle ||
        !generatedDescription ||
        generatedTitle.trim().length === 0 ||
        generatedDescription.trim().length === 0
      ) {
        setGenerationError(LABELS.copilotGenerateError)
        sendGenerateDescriptionEvent(
          `response_missing${!generatedTitle ? '_title' : ''}${!generatedDescription ? '_description' : ''}`,
        )
        return
      }

      sendGenerateDescriptionEvent()

      // Pass the generated title and description to the parent component
      onGenerate(generatedTitle, generatedDescription)
      setCopilotGenerateContext('')
    } catch (error) {
      setGenerationError(LABELS.copilotGenerateError)
      sendGenerateDescriptionEvent(error instanceof Error ? error.message : 'unknown_error')
      reportError(error)
    } finally {
      setIsGeneratingDescription(false)
    }
  }, [sendAnalyticsEvent, repoOwner, repoName, title, body, copilotGenerateContext, onGenerate])

  if (!isOpen) return null

  return (
    <Dialog
      title={LABELS.copilotGenerateDialogTitle}
      onClose={handleClose}
      footerButtons={[
        {
          buttonType: 'default',
          content: 'Cancel',
          onClick: handleClose,
          disabled: isGeneratingDescription,
        },
        {
          buttonType: 'primary',
          content: isGeneratingDescription ? (
            <Spinner size="small" className="mt-1 mr-1" />
          ) : (
            LABELS.copilotGenerateDialogGenerateButton
          ),
          onClick: handleGenerateDescription,
          disabled: isGeneratingDescription || !hasValidInput,
        },
      ]}
      returnFocusRef={returnFocusRef}
    >
      <div className={styles.dialogContent}>
        {generationError && (
          <Flash variant="danger">
            <AlertIcon />
            {generationError}
          </Flash>
        )}
        {!hasValidInput && (
          <Flash variant="warning">
            <AlertIcon />
            {LABELS.copilotGenerateValidationWarning}
          </Flash>
        )}
        <FormControl disabled={isGeneratingDescription}>
          <FormControl.Label>{LABELS.copilotGenerateDialogTextareaLabel}</FormControl.Label>
          <Textarea
            value={copilotGenerateContext}
            onChange={e => setCopilotGenerateContext(e.target.value)}
            placeholder={LABELS.copilotGenerateDialogTextareaPlaceholder}
            rows={6}
            block
            className="tmp-mt-3"
          />
        </FormControl>
      </div>
    </Dialog>
  )
}

function sendGenerateDescriptionEvent(
  error?: string | null,
  title?: string,
  body?: string,
  copilotGenerateContext?: string,
) {
  sendEvent('copilot.create_issue_description', {
    hasTitle: !!title,
    hasExistingDescription: !!body,
    hasAdditionalContext: !!copilotGenerateContext,
    error,
  })
}
